import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit2, Send, XCircle } from "lucide-react";
import logger from "@/lib/logger";

export default function ReportConfigTab() {
  const [reportConfigs, setReportConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [recipientInput, setRecipientInput] = useState("");

  const fetchReportConfigs = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/config");
      setReportConfigs(data.configs || []);
    } catch (err) { logger.error("Failed to fetch report configs:", err); }
  }, []);

  useEffect(() => { fetchReportConfigs(); }, [fetchReportConfigs]);

  const handleSaveConfig = async (cfg) => {
    try {
      await api.put(`/reports/config/${cfg.register_key}`, {
        enabled: cfg.enabled,
        frequency: cfg.frequency,
        recipients: cfg.recipients,
        sender_email: cfg.sender_email,
      });
      toast.success("Report config saved");
      setEditingConfig(null);
      fetchReportConfigs();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSendNow = async (registerKey) => {
    try {
      const { data } = await api.post(`/reports/config/${registerKey}/send-now`, {});
      toast.success(data.message || "Report sent");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
  };

  return (
    <>
      <Card className="mt-4 p-5" data-testid="report-config-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Report Email Configuration</h3>
            <p className="text-xs text-gray-500">Configure auto-send schedules and recipients for each F-GAT report</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReportConfigs} className="h-8 text-xs">Refresh</Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#1B3C87]/10">
                <TableHead className="text-xs uppercase font-bold">F-GAT No.</TableHead>
                <TableHead className="text-xs uppercase font-bold">Report Title</TableHead>
                <TableHead className="text-xs uppercase font-bold">Status</TableHead>
                <TableHead className="text-xs uppercase font-bold">Frequency</TableHead>
                <TableHead className="text-xs uppercase font-bold">Recipients</TableHead>
                <TableHead className="text-xs uppercase font-bold w-40">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportConfigs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No report configs found. Open Reports page first to initialize.</TableCell></TableRow>
              )}
              {reportConfigs.map(cfg => (
                <TableRow key={cfg.register_key} className="hover:bg-gray-50">
                  <TableCell className="font-mono text-xs text-[#E31E24] font-bold">{cfg.fgat_no}</TableCell>
                  <TableCell className="text-xs font-medium max-w-[200px] truncate">{cfg.title}</TableCell>
                  <TableCell>
                    <Badge className={cfg.enabled ? "bg-emerald-600" : "bg-gray-400"} variant="default">
                      {cfg.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs capitalize">{cfg.frequency?.replace("_", " ") || "On Demand"}</TableCell>
                  <TableCell className="text-xs">{(cfg.recipients || []).length > 0 ? cfg.recipients.join(", ") : "None"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => { setEditingConfig({ ...cfg }); setRecipientInput(""); }} data-testid={`edit-config-${cfg.register_key}`}>
                        <Edit2 size={12} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => handleSendNow(cfg.register_key)} data-testid={`send-now-${cfg.register_key}`}>
                        <Send size={12} className="mr-1" /> Send Now
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-3 p-3 bg-blue-50 rounded text-sm text-blue-700">
          Sender: <strong>mis@singhaniainternational.com</strong> · Email sending is MOCKED in Phase 1. Logs are saved for verification.
        </div>
      </Card>

      <Dialog open={!!editingConfig} onOpenChange={() => setEditingConfig(null)}>
        <DialogContent className="max-w-md" data-testid="edit-config-dialog">
          <DialogHeader>
            <DialogTitle>
              <span className="text-xs text-[#E31E24] font-mono mr-2">{editingConfig?.fgat_no}</span>
              Report Config
            </DialogTitle>
          </DialogHeader>
          {editingConfig && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Status</Label>
                <Select value={editingConfig.enabled ? "enabled" : "disabled"} onValueChange={v => setEditingConfig(p => ({ ...p, enabled: v === "enabled" }))}>
                  <SelectTrigger className="h-11" data-testid="config-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enabled">Enabled</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Frequency</Label>
                <Select value={editingConfig.frequency || "on_demand"} onValueChange={v => setEditingConfig(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger className="h-11" data-testid="config-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_demand">On Demand</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Sender Email</Label>
                <Input value={editingConfig.sender_email || "mis@singhaniainternational.com"} onChange={e => setEditingConfig(p => ({ ...p, sender_email: e.target.value }))} className="h-11" data-testid="config-sender" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Recipients</Label>
                <div className="flex gap-1">
                  <Input
                    placeholder="Enter email address"
                    value={recipientInput}
                    onChange={e => setRecipientInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && recipientInput.trim()) {
                        e.preventDefault();
                        setEditingConfig(p => ({ ...p, recipients: [...(p.recipients || []), recipientInput.trim()] }));
                        setRecipientInput("");
                      }
                    }}
                    className="h-11"
                    data-testid="config-recipient-input"
                  />
                  <Button variant="outline" className="h-11 px-3" onClick={() => {
                    if (recipientInput.trim()) {
                      setEditingConfig(p => ({ ...p, recipients: [...(p.recipients || []), recipientInput.trim()] }));
                      setRecipientInput("");
                    }
                  }}>
                    <Plus size={14} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(editingConfig.recipients || []).map((r, i) => (
                    <Badge key={`${r}-${i}`} variant="outline" className="text-xs pr-1">
                      {r}
                      <button className="ml-1 text-red-500 hover:text-red-700" onClick={() => setEditingConfig(p => ({
                        ...p,
                        recipients: (p.recipients || []).filter((_, idx) => idx !== i)
                      }))}>
                        <XCircle size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingConfig(null)}>Cancel</Button>
            <Button onClick={() => handleSaveConfig(editingConfig)} className="bg-[#1B3C87]" data-testid="save-config-btn">Save Config</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
